import { createSlice } from '@reduxjs/toolkit'

// Background Check list filters, split into the same model TruCheck uses:
//   - `draft`   — filter INPUTS the user is editing (search, company multi-select,
//                 exclude-demo, date range). Editing them does NOT run the query; nothing
//                 takes effect until `applyFilters` is dispatched.
//   - `applied` — the last submitted inputs; this is what actually drives the results.
//   - selectedStatuses / epicOrderedStatus / qaActionRequiredStatus / sortConfig /
//     currentPage / itemsPerPage — IMMEDIATE controls (status cards/chips, the Epic & QA
//     quick filters, column sort, pagination). They update the query straight away and are
//     NOT part of the Apply batch.
const initialDraft = {
    search: '',
    companyIds: [],
    excludeProdDemo: null,
    dateFrom: null,
    dateTo: null,
}

const cloneDraft = (d) => ({
    search: d.search,
    companyIds: [...d.companyIds],
    excludeProdDemo: d.excludeProdDemo,
    dateFrom: d.dateFrom,
    dateTo: d.dateTo,
})

const initialState = {
    draft: cloneDraft(initialDraft),
    applied: cloneDraft(initialDraft),
    selectedStatuses: [],
    epicOrderedStatus: '',
    qaActionRequiredStatus: '',
    sortConfig: { key: 'createdAt', direction: 'desc' },
    currentPage: 1,
    itemsPerPage: 10,
}

const homeFiltersSlice = createSlice({
    name: 'homeFilters',
    initialState,
    reducers: {
        // --- Draft (behind Apply) ---
        setDraftFilter: (state, action) => {
            state.draft[action.payload.key] = action.payload.value
        },
        // Commit the draft — the only thing (besides the immediate controls) that changes
        // what the list shows. Reset to page 1 on a new filter set.
        applyFilters: (state) => {
            state.applied = cloneDraft(state.draft)
            state.currentPage = 1
        },
        // Discard unapplied edits: snap the draft back to what's currently applied.
        resetDraft: (state) => {
            state.draft = cloneDraft(state.applied)
        },
        clearFilters: (state) => {
            state.draft = cloneDraft(initialDraft)
            state.applied = cloneDraft(initialDraft)
            state.selectedStatuses = []
            state.epicOrderedStatus = ''
            state.qaActionRequiredStatus = ''
            state.currentPage = 1
        },

        // --- Immediate controls (take effect at once; reset to page 1) ---
        setSelectedStatuses: (state, action) => {
            state.selectedStatuses = action.payload
            state.currentPage = 1
        },
        setEpicOrderedStatus: (state, action) => {
            state.epicOrderedStatus = action.payload
            state.currentPage = 1
        },
        setQaActionRequiredStatus: (state, action) => {
            state.qaActionRequiredStatus = action.payload
            state.currentPage = 1
        },
        setSortConfig: (state, action) => {
            state.sortConfig = action.payload
            state.currentPage = 1
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload
        },
        setItemsPerPage: (state, action) => {
            state.itemsPerPage = action.payload
            state.currentPage = 1
        },
    },
})

export const {
    setDraftFilter,
    applyFilters,
    resetDraft,
    clearFilters,
    setSelectedStatuses,
    setEpicOrderedStatus,
    setQaActionRequiredStatus,
    setSortConfig,
    setCurrentPage,
    setItemsPerPage,
} = homeFiltersSlice.actions

export default homeFiltersSlice.reducer
